import React from 'react';
import {
  ArrowDownUp,
  Users,
  ShoppingCart,
  CreditCard,
  TrendingUp,
  CalendarRange,
  MousePointerClick,
} from 'lucide-react';
import { GuideModal, GuideSection, GuideItem, InfoCallout } from '../ui/tour/guide';

interface ClubGuideModalProps {
  isOpen: boolean;
  onClose: () => void;
}

/**
 * راهنمای کاملِ «داشبورد جامع باشگاه مشتریان».
 * محتوا دقیقاً بر اساس ستون‌های واقعیِ دو جدولِ MonthlyPerformanceTable
 * (پارامترهای باشگاه و پارامترهای فروش) نوشته شده است.
 * با کیتِ مشترکِ ui/tour/guide ساخته شده تا با راهنمای داشبورد ارزیابی یکدست باشد.
 */
const ClubGuideModal: React.FC<ClubGuideModalProps> = ({ isOpen, onClose }) => (
  <GuideModal isOpen={isOpen} onClose={onClose} title="راهنمای کامل داشبورد باشگاه مشتریان">
    {/* مقدمه */}
    <section>
      <p className="text-text-secondary">
        این داشبورد عملکردِ{' '}
        <span className="font-semibold text-text-primary">باشگاه مشتریان</span> را در دو
        جدولِ ماهانه نشان می‌دهد: <b>پارامترهای باشگاه</b> (اعتبار، مشتریان یکتا و
        اشتراک‌گیری) و <b>پارامترهای فروش</b> (مبلغ، میانگین‌ها و رشد). هر <b>سطر</b> یک
        ماه است و هر <b>ستون</b> یک شاخص.
      </p>
    </section>

    {/* کنترل‌های سرستون */}
    <GuideSection icon={<ArrowDownUp size={20} />} title="کنترل‌های هر ستون">
      <ul className="space-y-3">
        <GuideItem label="مرتب‌سازی">
          با دکمه‌ی «مرتب» جدول را بر اساس همان ستون مرتب می‌کنید؛ کلیک اول نزولی و کلیک
          دوم صعودی. فلش جهتِ فعلی را نشان می‌دهد.
        </GuideItem>
        <GuideItem label="نمودار">
          با دکمه‌ی «نمودار» در سرستون، روندِ همان شاخص در طول ماه‌ها به‌صورت نمودار نمایش
          داده می‌شود.
        </GuideItem>
      </ul>
    </GuideSection>

    {/* ستون‌های مشترک */}
    <GuideSection icon={<Users size={20} />} title="ستون‌های مشترکِ هر دو جدول">
      <ul className="space-y-3">
        <GuideItem label="ماه">ماه و سالِ هر سطر؛ مبنای روندِ ماه‌به‌ماه.</GuideItem>
        <GuideItem label="اشتراک‌گیری">
          درصدِ فاکتورهایی که اطلاعاتِ تماسِ مشتری روی آن‌ها ثبت شده است. کنارِ این ستون یک
          <b> آیکونِ وضعیت</b> نمایش داده می‌شود؛ هرچه به ۱۰۰٪ نزدیک‌تر باشد، عملکرد بهتر.
        </GuideItem>
        <GuideItem label="تعداد سفارشات">
          تعداد کلِ فاکتورها (سفارش‌ها) در آن ماه؛ مبنای محاسبه‌ی بیشترِ نسبت‌ها و میانگین‌ها.
        </GuideItem>
      </ul>
    </GuideSection>

    {/* جدول ۱: پارامترهای باشگاه */}
    <GuideSection icon={<CreditCard size={20} />} title="۱) جدول پارامترهای باشگاه">
      <p className="text-text-secondary mb-3">
        تمرکزِ این جدول بر اعتبار و مشتریانِ باشگاه است:
      </p>
      <ul className="space-y-3">
        <GuideItem label="تعداد سفارش‌های با اعتبار">
          تعدادِ سفارش‌هایی که در آن‌ها از اعتبارِ باشگاه استفاده شده است.
        </GuideItem>
        <GuideItem label="مبلغ سفارش‌های با اعتبار">
          مجموعِ مبلغِ سفارش‌هایی که با اعتبار ثبت شده‌اند (به واحدِ پول).
        </GuideItem>
        <GuideItem label="مبلغ اعتبار مصرفی">
          مقدارِ اعتباری که واقعاً در آن ماه خرج شده است (به واحدِ پول).
        </GuideItem>
        <GuideItem label="تعداد مشتریان یکتا">
          تعدادِ مشتریانِ متمایز (بدونِ تکرار) که در آن ماه خرید داشته‌اند.
        </GuideItem>
        <GuideItem label="درصد سفارش‌های با اعتبار">
          سهمِ سفارش‌های با اعتبار از کلِ سفارش‌ها (= تعداد سفارش‌های با اعتبار ÷ تعداد
          سفارشات).
        </GuideItem>
        <GuideItem label="درصد اعتبار استفاده شده">
          نسبتِ اعتبارِ مصرف‌شده؛ نشان می‌دهد اعتبارِ باشگاه چقدر فعال استفاده می‌شود.
        </GuideItem>
      </ul>
    </GuideSection>

    {/* جدول ۲: پارامترهای فروش */}
    <GuideSection icon={<ShoppingCart size={20} />} title="۲) جدول پارامترهای فروش">
      <p className="text-text-secondary mb-3">
        تمرکزِ این جدول بر مبلغ، میانگین‌ها و رشدِ فروش است:
      </p>
      <ul className="space-y-3">
        <GuideItem label="میزان فروش">
          مجموعِ مبلغِ فروش در آن ماه (به واحدِ پول).
        </GuideItem>
        <GuideItem label="مبلغ هر سفارش">
          میانگینِ مبلغِ هر سفارش (= میزان فروش ÷ تعداد سفارشات).
        </GuideItem>
        <GuideItem label="قیمت هر واحد کالا">
          میانگینِ قیمتِ هر قلم کالا در آن ماه.
        </GuideItem>
        <GuideItem label="تعداد اقلام هر سفارش">
          میانگینِ تعدادِ اقلام در هر سفارش؛ نشان‌دهنده‌ی اندازه‌ی سبدِ خرید.
        </GuideItem>
      </ul>
    </GuideSection>

    {/* ستون‌های رشد */}
    <GuideSection icon={<TrendingUp size={20} />} title="ستون‌های رشد (نسبت به ماه قبل)">
      <p className="text-text-secondary mb-3">
        این ستون‌ها تغییرِ هر شاخص را نسبت به ماهِ قبل می‌سنجند: <b>سبز/↗</b> یعنی رشد و
        <b> قرمز/↘</b> یعنی کاهش.
      </p>
      <ul className="space-y-3">
        <GuideItem label="رشد تعداد سفارش">تغییرِ «تعداد سفارشات» نسبت به ماه قبل (٪).</GuideItem>
        <GuideItem label="رشد میزان فروش">تغییرِ «میزان فروش» نسبت به ماه قبل (٪).</GuideItem>
        <GuideItem label="رشد قیمت">تغییرِ «قیمت هر واحد کالا» نسبت به ماه قبل (٪).</GuideItem>
        <GuideItem label="رشد سبد">تغییرِ «تعداد اقلام هر سفارش» (اندازه‌ی سبد) نسبت به ماه قبل (٪).</GuideItem>
      </ul>
      <InfoCallout icon={<MousePointerClick size={18} />}>
        برای دیدنِ روندِ هر شاخص، روی دکمه‌ی «نمودار» همان ستون بزنید.
      </InfoCallout>
    </GuideSection>

    {/* بازه‌ی زمانی */}
    <GuideSection icon={<CalendarRange size={20} />} title="بازه‌ی زمانی">
      <p className="text-text-secondary">
        مقادیرِ هر دو جدول بر اساس بازه‌ی زمانیِ انتخاب‌شده‌ی بالای صفحه محاسبه می‌شوند؛ با
        تغییرِ بازه، سطرهای جدول و نمودارها به‌روزرسانی می‌شوند.
      </p>
    </GuideSection>
  </GuideModal>
);

export default ClubGuideModal;
